import { ActionRowBuilder, Collection, ColorResolvable, EmbedBuilder, Message } from "discord.js";
import { Bot, ButtonBuilder, Emoji, IMultiPageEmbedData, IMultiPageEmbedManager } from "..";
import { MultiPageEmbed } from "../structures/multipageembed";
import { uuid } from "@backs/utils";

export default class MultiPageEmbedManager<Configs extends Record<string, any>, Emojis extends Record<string, Emoji>, Colors extends Record<string, ColorResolvable>> implements IMultiPageEmbedManager {
    public client: Bot<Configs, Emojis, Colors>;

    constructor(client: Bot<Configs, Emojis, Colors>) {
        this.client = client;

        this.client.on("interactionCreate", async (interaction) => {
            if (interaction.isButton()) {
                const args = this.client.commands.parseArgs(interaction.customId, ["command", "embedId"]);

                if (
                    !args.command || !args.embedId ||
                    (args.userId && args.userId !== interaction.user.id)
                ) return await interaction.update({});
                if (args.id === "multipageembed") {
                    await interaction.deferUpdate();
                    const shouldUpdate = this.handle(args.embedId, args.command!);

                    if (shouldUpdate === true) {
                        const handler = this.handlers.get(args.embedId)!;
                        const embed = handler.embed.embeds[handler.index];
                        const row = new ActionRowBuilder<ButtonBuilder>()
                        if (handler.embed.jump) row.addComponents(
                            new ButtonBuilder()
                            .setCustomId("multipageembed")
                            .setUserId(handler.userId)
                            .setArguments("jump-start", args.embedId)
                            .setEmoji("⏮️")
                            .setStyle("gray")
                            .setDisabled(handler.index === 0)
                        )
                        row.addComponents(
                            new ButtonBuilder()
                            .setCustomId("multipageembed")
                            .setUserId(handler.userId)
                            .setArguments("previous", args.embedId)
                            .setEmoji("⏪")
                            .setStyle("gray")
                            .setDisabled(handler.index === 0 && !handler.embed.canLoop),
                            new ButtonBuilder()
                            .setCustomId("multipageembed")
                            .setUserId(handler.userId)
                            .setArguments("next", args.embedId)
                            .setEmoji("⏩")
                            .setStyle("gray")
                            .setDisabled(handler.index === handler.embed.embeds.length - 1 && !handler.embed.canLoop)
                        )
                        if (handler.embed.jump) row.addComponents(
                            new ButtonBuilder()
                            .setCustomId("multipageembed")
                            .setUserId(handler.userId)
                            .setArguments("jump-end", args.embedId)
                            .setEmoji("⏭️")
                            .setStyle("gray")
                            .setDisabled(handler.index === handler.embed.embeds.length - 1)
                        )
                        await interaction.editReply({
                            embeds: [embed, ...interaction.message.embeds.slice(1)],
                            components: [row, ...interaction.message.components.slice(1)]
                        });
                    } else if (shouldUpdate === false) {
                        await interaction.editReply({});
                    } else {
                        await interaction.editReply({
                            components: [...interaction.message.components.slice(1)]
                        });
                    
                    }
                }
            }
        })
    }

    public embeds: Collection<string, Omit<IMultiPageEmbedData, "id">> = new Collection();
    private handlers: Collection<string, {
        embed: MultiPageEmbed;
        index: number;
        expires: number;
        userId: string;
    }> = new Collection();

    create(data: IMultiPageEmbedData, createEmbed: true, userId: string): MultiPageEmbed;
    create(data: IMultiPageEmbedData, createEmbed: false): undefined;
    create(data: IMultiPageEmbedData, createEmbed: boolean = true, userId?: string): MultiPageEmbed | undefined {
        const embedId = data.id ?? uuid();
        this.embeds.set(embedId, {
            ...data,
            id: undefined
        } as Omit<IMultiPageEmbedData, "id">);

        const id = uuid();
        if (!createEmbed) return;
        const embed = new MultiPageEmbed({
            ...data,
            id
        });
        const expires = embed.expires;
        this.handlers.set(id, {
            embed,
            index: data.startingPage ?? 0,
            expires: Date.now() + expires,
            userId: userId!
        });
        setTimeout(() => this.handlers.delete(id), expires);
        return embed;
    }

    get(embedId: string, userId: string) {
        const embedData = this.embeds.get(embedId);
        if (!embedData) return;
        const id = uuid();

        const embed = new MultiPageEmbed({
            ...embedData,
            id
        });
        const expires = embed.expires;
        this.handlers.set(id, {
            embed,
            index: embedData.startingPage ?? 0,
            expires: Date.now() + expires,
            userId: userId!
        });
        setTimeout(() => this.handlers.delete(id), expires);
        return embed;
    }

    delete(embedId: string): void {
        this.embeds.delete(embedId);
    }

    private handle(embedId: string, command: string): boolean | undefined {
        const handler = this.handlers.get(embedId);
        if (!handler) return;

        switch (command) {
            case "jump-start":
                if (!handler.embed.jump) return false;
                handler.index = 0;
                break;
            case "previous":
                if (handler.index === 0) {
                    if (handler.embed.canLoop) handler.index = handler.embed.embeds.length - 1;
                } else handler.index--;
                break;
            case "next":
                if (handler.index === handler.embed.embeds.length - 1) {
                    if (handler.embed.canLoop) handler.index = 0;
                } else handler.index++;
                break;
            case "jump-end":
                if (!handler.embed.jump) return false;
                handler.index = handler.embed.embeds.length - 1;
                break;
        }

        return true
    }
}